import { Box, Flex, IconDelete, IconEditBoxOutlined, IconFolderFilled, ImageButton, Spinner, toast, Tooltip } from '@gmg/gmg-react-components';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AppContext from '../../AppContext';
import { useDeleteFolder } from '../../graphql/useDeleteFolder';
import { useGetFolderDetails } from '../../graphql/useGetFolderDetails';
import { FolderListItem } from '../../viewModels/Folder';
import FolderModalContainer from './FolderModalContainer';

export interface DataManagementActionsProps {
    folder: FolderListItem;
    siblingFolderNames: Array<string>;
    onDeleteFolder: () => void;
    onEditFolder: (updatedFolder: FolderListItem) => void;
    groups: Map<string, string>;
    orgId: string;
};

const ActionButtonWrapper = styled(Flex)`
    gap: 16px;
    justify-content: flex-end;    
`;

const DataManagementActions: FC<DataManagementActionsProps> = props => {
    const context = useContext(AppContext);
    const { t } = useTranslation();

    const deleteFolder = useDeleteFolder();
    const [isDeleteIconClicked, setIsDeleteIconClicked] = useState(false);

    const { data, isFetching } = useGetFolderDetails(isDeleteIconClicked ? props.folder.id : undefined);
    useEffect(() => {
        if (!data) return;

        setIsDeleteIconClicked(false);

        if (data.isEmpty) {
            context.onShowModal('infoModal', {
                title: t('DataManagementActions.removeFolder.modalTitle_title', 'Delete Folder'),
                content:
                    <Box>
                        <p>{t('DataManagementActions.removeFolder.question_desc', {
                            defaultValue: 'Are you sure you want to delete the folder {{folderName}}?',
                            folderName: props.folder.name,
                        })}</p>
                        <p className="px10_400_weak">
                            {t('DataManagementActions.removeFolder.information_desc', 'By deleting this folder, all assigned users will loose access.')}
                        </p>
                    </Box>,
                onOkClick: handleDelete,
                btnOkCaption: t('ButtonTitles.action_delete_btn', 'Delete'),
            });
        } else {
            context.onShowModal('infoModal', {
                title: t('DataManagementActions.removeFolder.modalTitle_title', 'Delete Folder'),
                // Note that this is intentionally not translated as later, also empty folders should be deletable
                content:
                    <Box>
                        <p>Sorry, at this point in time only empty folders can be deleted.</p>
                        <p>Folder {props.folder.name} contains files or subfolders.</p>
                    </Box>,
            });
        }
        setIsDeleteIconClicked(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleDelete = () => {
        const wrapperPromise = new Promise<void>(async (resolve, reject) => {
            try {
                await deleteFolder.mutateAsync({ folderIds: [props.folder.id] });
            } catch (e: any) {
                reject(e);
            }

            props.onDeleteFolder();
            resolve();
        });

        void toast.promise(wrapperPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: t('DataManagementActions.removeFolder.toast_success_desc', {
                defaultValue: 'You have successfully deleted {{folderName}}',
                folderName: props.folder.name,
            }),
            error: () => t('Common.Toast.Error', 'An error occurred'),
        });
    };

    const handleDeleteClick = (ev: any) => {
        ev.stopPropagation();
        setIsDeleteIconClicked(true);
        // hook aktivieren
        // danach im effekt ergebnis evaluieren und entsprechend triggern
    };
    const handleEditClick = (ev: any) => {
        // ev.stopPropagation();
        context.onShowModal('inputModal', {
            title: t('AddOrEditFolderModal.editFolder_title', 'Edit folder'),
            titleIcon: <IconFolderFilled />,
            content: <FolderModalContainer
                folderToEdit={props.folder}
                onSubmit={props.onEditFolder}
                siblingFolderNames={props.siblingFolderNames}
                orgId={props.orgId}
                groups={props.groups}
            />,
        });
    };

    return (
        <ActionButtonWrapper>
            <Tooltip message={t('ButtonTitles.action_edit_btn')}>
                <ImageButton
                    icon={<IconEditBoxOutlined />}
                    onClick={handleEditClick}
                    aria-label="edit icon"
                />
            </Tooltip>
            {!isFetching
                ? <Tooltip message={t('ButtonTitles.action_delete_btn')}>
                    <ImageButton
                        icon={<IconDelete />}
                        aria-label="delete icon"
                        onClick={handleDeleteClick}
                    />
                </Tooltip>
                : <Spinner svgSize="14px" />
            }
        </ActionButtonWrapper>
    );
};

export default DataManagementActions;